import { RoomType, User } from "@prisma/client";

import { Link, useSearchParams } from "react-router";
import { useEffect, useMemo, useState } from "react";
import ButtonBlock from "@app/components/ButtonBlock";
import ButtonWrite from "@app/components/ButtonWrite";
import ProfileButton from "@app/components/ProfileButton";
import { UsersGetResponse } from "@api/src/types/responses";

export default function Users() {
  const [users, setUsers] = useState<UsersGetResponse["data"]["users"]>([]);
  const [numberOfUsersInTheRoom, setNumberOfUsersInTheRoom] =
    useState<UsersGetResponse["data"]["numberOfUsersInTheRoom"]>(0);

  const [blockedUsers, setBlockedUsers] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<string>("");

  const [searchParams] = useSearchParams();
  const qRoomId = searchParams.get("qRoomId");
  const qRoomType = searchParams.get("qRoomType");

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/users?qRoomId=${qRoomId}`, {
      credentials: "include",
      method: "GET",
    })
      .then((response) => response.json() as Promise<UsersGetResponse>)
      .then((response) => {
        if (response.ok) {
          setUsers(response.data.users);
          setNumberOfUsersInTheRoom(response.data.numberOfUsersInTheRoom);
        } else if (response.error) {
          alert(response.error);
        }
      });
  }, [qRoomId]);

  const filteredUsers = useMemo(() => {
    if (!filter) {
      if (qRoomId && qRoomType === RoomType.DIRECT) {
        return users.filter((user) => user.isInTheRoom);
      }
      if (qRoomType === RoomType.DIRECT) {
        return [];
      }
    }
    return users.filter((user) => {
      if (blockedUsers[user.id]) return false;
      if (user.blocked) return false;
      if (user.isDeleted) return false;
      return (
        user.firstName?.toLowerCase().includes(filter.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(filter.toLowerCase())
      );
    });
  }, [filter, users, qRoomId, qRoomType, blockedUsers]);

  const usersInTheRoom = useMemo(() => {
    return users
      .filter((user) => user.isInTheRoom)
      .map((user, index, array) => {
        const name = `${user.firstName} ${user.lastName}`;
        if (index === array.length - 1) {
          return name;
        }
        if (index === array.length - 2) {
          return `${name} et `;
        }
        return `${name}, `;
      });
  }, [users]);

  const [showDropdown, setShowDropdown] = useState("");

  return (
    <div className="relative mt-4 flex w-full flex-col overflow-y-auto md:px-6">
      <div
        className="drop-shadow-xs flex w-full rounded-lg border border-gray-200 bg-white p-4"
        suppressHydrationWarning
      >
        <span className="mr-2 inline-flex size-8 shrink-0 items-center justify-center text-center text-2xl">🕵️‍♀️</span>
        <input
          type="search"
          name="search"
          className="flex-grow overflow-x-auto scroll-smooth whitespace-nowrap rounded-full  border border-sky-900 border-opacity-30 px-4 text-left text-sky-900"
          placeholder="Recherchez un utilisateur..."
          onChange={(e) => {
            setFilter(e.target.value);
          }}
        />
      </div>
      {qRoomId && qRoomType === RoomType.DIRECT && (
        <>
          <p className="mt-2 text-sky-900 text-xs">
            Déjà dans le groupe de discussion&nbsp;({usersInTheRoom.length})&nbsp;: {usersInTheRoom}
          </p>
          <div className="flex items-center">
            <Link
              to={`/?qRoomId=${qRoomId}&qRoomType=${RoomType.DIRECT}`}
              className="w-max cursor-pointer py-1 text-xs text-sky-900 underline"
            >
              {"<"} Retour au groupe
            </Link>
          </div>
        </>
      )}
      <ul role="list" className="w-full overflow-y-auto overflow-x-hidden">
        {filteredUsers.map((user) => {
          let place = "";
          if (user.hopital && user.service) {
            place += `${user.hopital} - ${user.service}`;
          } else if (user.hopital) {
            place += user.hopital;
          } else if (user.service) {
            place += user.service;
          }
          return (
            <li
              key={user.plausible_id}
              className="mx-auto mt-2 flex w-full items-center justify-start rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm md:mt-4"
            >
              <Link to={`/profile/${user.plausible_id}`} className="flex min-w-0 gap-x-4">
                <ProfileButton user={user} />
                <div className="min-w-0 flex-auto">
                  <p className="text-sm font-semibold leading-6 text-gray-900">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="mt-1 truncate text-xs leading-5 text-gray-500">{user.specialite}</p>
                  <p className="mt-1 truncate text-xs leading-5 text-gray-500">{place}</p>
                </div>
              </Link>
              <div className="ml-auto flex flex-col items-end md:items-center space-y-2 md:flex-row md:space-x-2 md:space-y-0">
                <div className="relative flex flex-row items-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (showDropdown === user.plausible_id) {
                        setShowDropdown("");
                      } else {
                        setShowDropdown(user.plausible_id);
                      }
                    }}
                    className="text-gray-500 hover:text-sky-900"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                  {showDropdown === user.plausible_id && (
                    <div className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                      <ButtonBlock
                        user={user as unknown as User}
                        className="py-2 px-4 w-full text-left text-sm"
                        onBlocked={() => {
                          setShowDropdown("");
                          setBlockedUsers((prev) => ({ ...prev, [user.id]: true }));
                        }}
                      />
                    </div>
                  )}
                </div>
                <ButtonWrite
                  user={user}
                  isInTheRoom={user.isInTheRoom}
                  numberOfUsersInTheRoom={numberOfUsersInTheRoom}
                />
              </div>
            </li>
          );
        })}
        {!filter && (
          <li>
            <p className="mx-auto mt-4 max-w-prose py-4 text-center text-sky-900">
              <>
                {qRoomId && qRoomType === RoomType.DIRECT && <>Pour ajouter un utilisateur,</>}
                {!qRoomId && (
                  <>
                    Pour démarrer une discussion
                    <br />
                    avec un utilisateur,
                  </>
                )}
                <br />
                tapez son nom ou son prénom
                <br />
                dans la barre de recherche
                <br />
                🧐
              </>
            </p>
          </li>
        )}
      </ul>
      {filter && !filteredUsers.length && (
        <p className="mx-auto mt-4 max-w-prose py-4 text-center text-sky-900">Aucun utilisateur trouvé 🤔</p>
      )}
    </div>
  );
}
